/**
 * 🚨 RESCUE ADAPTER - ConfigurationService
 * Adaptador inteligente que rescata los componentes admin existentes
 * Usa ConfigurationManager internamente + endpoints directos para datos específicos
 */

import ConfigurationManager from './ConfigurationManager';
import ApiClient from '../../infrastructure/api/apiClient';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

// ===============================================
// 🎯 INTERFACES DE CONFIGURACIÓN ESPECÍFICAS
// ===============================================

export interface DevelopmentConfig {
  mode: boolean;
  allowAdminOnlyAccess: boolean;
  bypassEmailVerification: boolean;
  requireEmailVerification: boolean;
  emailVerificationTimeout: number;
}

export interface ModerationConfig {
  auto_approval: boolean;
  review_threshold: number;
  enable_user_reports: boolean;
  auto_suspend_threshold: number;
  manual_review_required: boolean;
  userStrikesThreshold: number;
  lowStockThreshold: number;
  contactScorePenalty: number;
  businessScoreBonus: number;
  contactPenaltyHeavy: number;
  minimumContactScore: number;
  scoreDifferenceThreshold: number;
  consecutiveNumbersLimit: number;
  numbersWithContextLimit: number;
}

export interface SecurityConfig {
  password_rules: {
    min_length: number;
    require_uppercase: boolean;
    require_lowercase: boolean;
    require_numbers: boolean;
    require_special_chars: boolean;
  };
  max_login_attempts: number;
  lockout_duration: number;
  passwordMinLength: number;
  passwordRequireSpecial: boolean;
  passwordRequireUppercase: boolean;
  passwordRequireNumbers: boolean;
  accountLockAttempts: number;
  sessionTimeout: number; // minutos - unified field from backend
  requireEmailVerification: boolean;
  enableTwoFactor: boolean;
  adminIpRestriction: string;
}

export interface RatingConfig {
  min_rating: number;
  max_rating: number;
  allow_anonymous: boolean;
  require_purchase: boolean;
  enable_reviews: boolean;
  auto_publish_reviews: boolean;
  auto_approve_all: boolean;
  auto_approve_threshold: number;
  'ratings.auto_approve_all': boolean;
  'ratings.auto_approve_threshold': number;
}

// ===============================================
// 🎯 RESPONSE INTERFACES (API Compatible)
// ===============================================

interface ConfigurationResponse<T> {
  status: 'success' | 'error';
  data?: T;
  message?: string;
}

// ===============================================
// 🚨 CONFIGURATION SERVICE ADAPTER
// ===============================================

// Interface para Volume Discount (requerido por VolumeDiscountManager)
export interface VolumeDiscountConfig {
  enabled: boolean;
  stackable: boolean;
  show_savings_message: boolean;
  default_tiers: Array<{
    quantity: number;
    discount: number;
    label: string;
  }>;
}

class ConfigurationService {
  constructor() {
    // ConfigurationManager se usa a través del singleton cuando es necesario
  }

  // 🔧 DEVELOPMENT CONFIGURATIONS
  async getDevelopmentConfigs(): Promise<ConfigurationResponse<DevelopmentConfig>> {
    try {
      const response: any = await ApiClient.get(API_ENDPOINTS.ADMIN.CONFIGURATIONS.DEVELOPMENT);
      const responseData = response?.data ?? response ?? null;

      if (responseData && typeof responseData === 'object') {
        const config: DevelopmentConfig = {
          mode: responseData.mode ?? false,
          allowAdminOnlyAccess:
            responseData.allowAdminOnlyAccess ?? responseData.allow_admin_only_access ?? false,
          bypassEmailVerification:
            responseData.bypassEmailVerification ?? responseData.bypass_email_verification ?? true,
          requireEmailVerification:
            responseData.requireEmailVerification ?? responseData.require_email_verification ?? false,
          emailVerificationTimeout:
            responseData.emailVerificationTimeout ?? responseData.email_verification_timeout ?? 24,
        };

        return {
          status: 'success',
          data: config
        };
      }

      // Fallback con valores por defecto
        return {
          status: 'success',
          data: {
            mode: false,
            allowAdminOnlyAccess: false,
            bypassEmailVerification: false,
            requireEmailVerification: true,
            emailVerificationTimeout: 24,
          }
        };
    } catch (error) {
      console.error('Error getting development configs:', error);
      return {
        status: 'error',
        message: 'Error al obtener configuraciones de desarrollo'
      };
    }
  }

  async updateDevelopmentConfigs(config: DevelopmentConfig): Promise<ConfigurationResponse<DevelopmentConfig>> {
    try {
      await ApiClient.post(API_ENDPOINTS.ADMIN.CONFIGURATIONS.DEVELOPMENT, {
        mode: config.mode,
        allowAdminOnlyAccess: config.allowAdminOnlyAccess,
        bypassEmailVerification: config.bypassEmailVerification,
        requireEmailVerification: config.requireEmailVerification,
        emailVerificationTimeout: config.emailVerificationTimeout,
      });

      // ✅ INVALIDAR CACHE para propagar cambios inmediatamente
      ConfigurationManager.getInstance().invalidateCache();

      return {
        status: 'success',
        data: config,
        message: 'Configuración actualizada correctamente'
      };
    } catch (error) {
      console.error('Error updating development configs:', error);
      return {
        status: 'error',
        message: 'Error al actualizar configuraciones'
      };
    }
  }

  // 🔧 MODERATION CONFIGURATIONS  
  async getModerationConfigs(): Promise<ConfigurationResponse<ModerationConfig>> {
    try {
      const response: any = await ApiClient.get(API_ENDPOINTS.ADMIN.CONFIGURATIONS.MODERATION);
      const moderationData = response?.data ?? response ?? {};

      const config: ModerationConfig = {
        auto_approval: moderationData?.auto_approval ?? true,
        review_threshold: moderationData?.review_threshold ?? 3,
        enable_user_reports: moderationData?.enable_user_reports ?? true,
        auto_suspend_threshold: moderationData?.auto_suspend_threshold ?? 5,
        manual_review_required: moderationData?.manual_review_required ?? false,
        userStrikesThreshold:
          moderationData?.userStrikesThreshold ?? moderationData?.user_strikes_threshold ?? 3,
        lowStockThreshold:
          moderationData?.lowStockThreshold ?? moderationData?.low_stock_threshold ?? 5,
        contactScorePenalty:
          moderationData?.contactScorePenalty ?? moderationData?.contact_score_penalty ?? 10,
        businessScoreBonus:
          moderationData?.businessScoreBonus ?? moderationData?.business_score_bonus ?? 5,
        contactPenaltyHeavy:
          moderationData?.contactPenaltyHeavy ?? moderationData?.contact_penalty_heavy ?? 20,
        minimumContactScore:
          moderationData?.minimumContactScore ?? moderationData?.minimum_contact_score ?? 50,
        scoreDifferenceThreshold:
          moderationData?.scoreDifferenceThreshold ?? moderationData?.score_difference_threshold ?? 30,
        consecutiveNumbersLimit:
          moderationData?.consecutiveNumbersLimit ?? moderationData?.consecutive_numbers_limit ?? 3,
        numbersWithContextLimit:
          moderationData?.numbersWithContextLimit ?? moderationData?.numbers_with_context_limit ?? 5,
      };

      return {
        status: 'success',
        data: config
      };
    } catch (error) {
      console.error('Error getting moderation configs:', error);
      return {
        status: 'error',
        message: 'Error al obtener configuraciones de moderación'
      };
    }
  }

  async updateModerationConfigs(config: ModerationConfig): Promise<ConfigurationResponse<ModerationConfig>> {
    try {
      await ApiClient.post(API_ENDPOINTS.ADMIN.CONFIGURATIONS.MODERATION, {
        userStrikesThreshold: config.userStrikesThreshold,
        contactScorePenalty: config.contactScorePenalty,
        businessScoreBonus: config.businessScoreBonus,
        contactPenaltyHeavy: config.contactPenaltyHeavy,
        minimumContactScore: config.minimumContactScore,
        scoreDifferenceThreshold: config.scoreDifferenceThreshold,
        consecutiveNumbersLimit: config.consecutiveNumbersLimit,
        numbersWithContextLimit: config.numbersWithContextLimit,
        lowStockThreshold: config.lowStockThreshold,
      });

      // ✅ INVALIDAR CACHE para propagar cambios inmediatamente
      ConfigurationManager.getInstance().invalidateCache();

      return {
        status: 'success',
        data: config,
        message: 'Configuración de moderación actualizada'
      };
    } catch (error) {
      console.error('Error updating moderation configs:', error);
      return {
        status: 'error',
        message: 'Error al actualizar configuraciones de moderación'
      };
    }
  }

  // 🔧 SECURITY CONFIGURATIONS
  async getSecurityConfigs(): Promise<ConfigurationResponse<SecurityConfig>> {
    try {
      const response: any = await ApiClient.get(API_ENDPOINTS.ADMIN.CONFIGURATIONS.CATEGORY, {
        category: 'security',
      });
      const securityData = response?.data ?? response ?? {};

      const passwordMinLength =
        securityData.passwordMinLength ?? securityData.password_min_length ?? 8;
      const passwordRequireSpecial =
        securityData.passwordRequireSpecial ?? securityData.password_require_special ?? true;
      const passwordRequireUppercase =
        securityData.passwordRequireUppercase ?? securityData.password_require_uppercase ?? true;
      const passwordRequireNumbers =
        securityData.passwordRequireNumbers ?? securityData.password_require_numbers ?? true;
      const accountLockAttempts =
        securityData.accountLockAttempts ?? securityData.account_lock_attempts ?? 5;
      const sessionTimeout =
        securityData.sessionTimeout ??
        securityData.session_timeout ??
        parseInt(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || '120');
      const requireEmailVerification =
        securityData.requireEmailVerification ?? securityData.require_email_verification ?? true;
      const enableTwoFactor =
        securityData.enableTwoFactor ?? securityData.enable_two_factor ?? false;
      const adminIpRestrictionRaw =
        securityData.adminIpRestriction ?? securityData.admin_ip_restriction ?? '';
      const adminIpRestriction =
        typeof adminIpRestrictionRaw === 'string'
          ? adminIpRestrictionRaw
          : String(adminIpRestrictionRaw ?? '');

      const config: SecurityConfig = {
        password_rules: {
          min_length: passwordMinLength,
          require_uppercase: passwordRequireUppercase,
          require_lowercase: true,
          require_numbers: passwordRequireNumbers,
          require_special_chars: passwordRequireSpecial,
        },
        max_login_attempts: accountLockAttempts,
        lockout_duration: 900, // 15 minutos
        passwordMinLength,
        passwordRequireSpecial,
        passwordRequireUppercase,
        passwordRequireNumbers,
        accountLockAttempts,
        sessionTimeout,
        requireEmailVerification,
        enableTwoFactor,
        adminIpRestriction,
      };

      return {
        status: 'success',
        data: config
      };
    } catch (error) {
      console.error('Error getting security configs:', error);
      return {
        status: 'error',
        message: 'Error al obtener configuraciones de seguridad'
      };
    }
  }

  async updateSecurityConfigs(config: SecurityConfig): Promise<ConfigurationResponse<SecurityConfig>> {
    try {
      await ApiClient.post(API_ENDPOINTS.ADMIN.CONFIGURATIONS.CATEGORY, {
        category: 'security',
        configurations: {
          passwordMinLength: config.passwordMinLength,
          passwordRequireSpecial: config.passwordRequireSpecial,
          passwordRequireUppercase: config.passwordRequireUppercase,
          passwordRequireNumbers: config.passwordRequireNumbers,
          accountLockAttempts: config.accountLockAttempts,
          sessionTimeout: config.sessionTimeout,
          requireEmailVerification: config.requireEmailVerification,
          enableTwoFactor: config.enableTwoFactor,
          adminIpRestriction: config.adminIpRestriction ?? '',
        },
      });

      // ✅ INVALIDAR CACHE para propagar cambios inmediatamente
      ConfigurationManager.getInstance().invalidateCache();

      return {
        status: 'success',
        data: config,
        message: 'Configuración de seguridad actualizada'
      };
    } catch (error) {
      console.error('Error updating security configs:', error);
      return {
        status: 'error',
        message: 'Error al actualizar configuraciones de seguridad'
      };
    }
  }

  // 🔧 RATING CONFIGURATIONS
  async getRatingConfigs(): Promise<ConfigurationResponse<RatingConfig>> {
    try {
      const response: any = await ApiClient.get(API_ENDPOINTS.ADMIN.CONFIGURATIONS.RATINGS);
      const responseData = response?.data ?? response ?? {};
      const readValue = (value: any, fallback: any) => {
        if (value && typeof value === 'object' && 'value' in value) {
          return (value as {value: any}).value ?? fallback;
        }
        return value ?? fallback;
      };

      const autoApproveAll = readValue(
        responseData['ratings.auto_approve_all'] ?? responseData.auto_approve_all,
        false
      );
      const autoApproveThreshold = readValue(
        responseData['ratings.auto_approve_threshold'] ?? responseData.auto_approve_threshold,
        4
      );

      const config: RatingConfig = {
        min_rating: readValue(responseData.min_rating, 1),
        max_rating: readValue(responseData.max_rating, 5),
        allow_anonymous: readValue(responseData.allow_anonymous, false),
        require_purchase: readValue(responseData.require_purchase, true),
        enable_reviews: readValue(responseData.enable_reviews, true),
        auto_publish_reviews: readValue(responseData.auto_publish_reviews, false),
        auto_approve_all: autoApproveAll,
        auto_approve_threshold: autoApproveThreshold,
        'ratings.auto_approve_all': autoApproveAll,
        'ratings.auto_approve_threshold': autoApproveThreshold,
      };

      return {
        status: 'success',
        data: config
      };
    } catch (error) {
      console.error('Error getting rating configs:', error);
      return {
        status: 'error',
        message: 'Error al obtener configuraciones de calificaciones'
      };
    }
  }

  async updateRatingConfigs(config: RatingConfig): Promise<ConfigurationResponse<RatingConfig>> {
    try {
      await ApiClient.post(API_ENDPOINTS.ADMIN.CONFIGURATIONS.RATINGS, {
        auto_approve_all: config.auto_approve_all,
        auto_approve_threshold: config.auto_approve_threshold,
      });

      // ✅ INVALIDAR CACHE para propagar cambios inmediatamente
      ConfigurationManager.getInstance().invalidateCache();

      return {
        status: 'success',
        data: config,
        message: 'Configuración de calificaciones actualizada'
      };
    } catch (error) {
      console.error('Error updating rating configs:', error);
      return {
        status: 'error',
        message: 'Error al actualizar configuraciones de calificaciones'
      };
    }
  }

  // 🔧 VOLUME DISCOUNT CONFIGURATIONS (requerido por VolumeDiscountManager)
  async getVolumeDiscountConfigs(): Promise<ConfigurationResponse<VolumeDiscountConfig>> {
    try {
      const response: any = await ApiClient.get(API_ENDPOINTS.ADMIN.VOLUME_DISCOUNTS.CONFIGURATION);
      const responseData = response?.data ?? response ?? {};
      const defaultTiers = Array.isArray(responseData?.default_tiers)
        ? responseData.default_tiers
        : [
            { quantity: 3, discount: 5, label: "3+" },
            { quantity: 5, discount: 8, label: "5+" },
            { quantity: 10, discount: 15, label: "10+" }
          ];

      const config: VolumeDiscountConfig = {
        enabled: responseData?.enabled ?? true,
        stackable: responseData?.stackable ?? false,
        show_savings_message: responseData?.show_savings_message ?? true,
        default_tiers: defaultTiers,
      };

      return {
        status: 'success',
        data: config
      };
    } catch (error) {
      console.error('Error getting volume discount configs:', error);
      return {
        status: 'error',
        message: 'Error al obtener configuraciones de descuentos por volumen'
      };
    }
  }

  async updateVolumeDiscountConfigs(config: VolumeDiscountConfig): Promise<ConfigurationResponse<VolumeDiscountConfig>> {
    try {
      await ApiClient.post(API_ENDPOINTS.ADMIN.VOLUME_DISCOUNTS.CONFIGURATION, config);

      // ✅ INVALIDAR CACHE para propagar cambios inmediatamente
      ConfigurationManager.getInstance().invalidateCache();

      return {
        status: 'success',
        data: config,
        message: 'Configuración de descuentos por volumen actualizada'
      };
    } catch (error) {
      console.error('Error updating volume discount configs:', error);
      return {
        status: 'error',
        message: 'Error al actualizar configuraciones de descuentos por volumen'
      };
    }
  }

  // 🔧 RATING STATS AND ADDITIONAL METHODS (requerido por RatingConfiguration)
  async getRatingStats(): Promise<ConfigurationResponse<any>> {
    try {
      const response: any = await ApiClient.get(`${API_ENDPOINTS.ADMIN.CONFIGURATIONS.RATINGS}/stats`);
      
      return {
        status: 'success',
        data: response?.data || {}
      };
    } catch (error) {
      console.error('Error getting rating stats:', error);
      return {
        status: 'error',
        message: 'Error al obtener estadísticas de calificaciones'
      };
    }
  }

  async approveAllPendingRatings(): Promise<ConfigurationResponse<any>> {
    try {
      const response: any = await ApiClient.post(`${API_ENDPOINTS.ADMIN.CONFIGURATIONS.RATINGS}/approve-all`);
      
      return {
        status: 'success',
        data: response?.data || {},
        message: 'Todas las calificaciones pendientes han sido aprobadas'
      };
    } catch (error) {
      console.error('Error approving pending ratings:', error);
      return {
        status: 'error',
        message: 'Error al aprobar calificaciones pendientes'
      };
    }
  }
}

export default ConfigurationService;
